import React, { useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { pluralizeToString } from 'lib/string/pluralize'
import { buildRoomPackageOptionsViewOrder } from 'lib/offer/offerPageUtil'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import Group from 'components/utils/Group'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import FlashHotelRateOptionVariant from '../FlashHotelRateOptionVariant'
import Pane from 'components/Common/Pane'
import BodyText from 'components/Luxkit/Typography/BodyText'
import AccommodationMobileHeaderFilters from 'components/OfferPage/Accommodation/AccommodationMobileHeaderFilters'

const OptionsContainer = styled.div`
  padding: ${rem(12)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

interface Props {
  room: App.PackageRoomType;
  offer: App.Offer;
  airports?: Array<App.Airport>;
  packageOptions: Array<OfferPackageOptionView>;
  onClose?: () => void;
  onBack?: () => void;
}

function FlashHotelPackageSelectionModal(props: Props) {
  const {
    room,
    offer,
    airports,
    packageOptions,
    onClose,
    onBack,
  } = props

  const packageOptionsByRoom = useMemo(() => {
    return buildRoomPackageOptionsViewOrder(packageOptions)
  }, [packageOptions])

  const packageViews = useMemo(() => packageOptionsByRoom.find(option => option.roomId === room?.id)?.packageViews ?? [], [packageOptionsByRoom, room?.id])

  const destinationAirport = useMemo(() => airports?.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])

  return (
    <>
      <ModalHeader
        title="Select a package"
        onCloseButtonClick={onClose}
        onBackButtonClick={onBack}
      >
        <AccommodationMobileHeaderFilters />
      </ModalHeader>
      <ModalBody>
        <Pane background="neutral-six">
          <Group direction="vertical" gap={8}>
            {packageViews.length > 1 && (
              <OptionsContainer>
                <BodyText variant="small" colour="neutral-three">{pluralizeToString('package option', packageViews.length)} available</BodyText>
              </OptionsContainer>
            )}
            {packageViews.map((view, index) => <Pane key={view.package.uniqueKey}>
              <FlashHotelRateOptionVariant
                offer={offer}
                optionNumber={index + 1}
                packageView={view}
                flightDestination={destinationAirport}
                showOptionLabel={packageViews.length > 1}
              />
            </Pane>)}
          </Group>
        </Pane>
      </ModalBody>
    </>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  room: state.room,
  offer: state.offer,
  rooms: state.rooms,
  airports: appState.geo.airports,
  packageOptions: getDurationPackageOptions(state, appState),
}))(FlashHotelPackageSelectionModal)
